import { Component, Inject, OnDestroy } from '@angular/core';
import { ReplaySubject, takeUntil, finalize, catchError, EMPTY, of, switchMap, tap } from 'rxjs';
import { InvitationsService } from 'core/services/invitations.service';
import { AuthService } from 'core/services/auth.service';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { CustomMessageService } from 'core/services/custom-message.service';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';

@Component({
  selector: 'delete-referral',
  templateUrl: './delete-referral.component.html',
  styleUrl: './delete-referral.component.scss',
  standalone: true,
  imports: [KorviuLoaderComponent, TranslateModule, KorviuButtonComponent, NzModalModule]
})
export class DeleteReferralComponent implements OnDestroy {
  public isLoading: boolean;
  private readonly _onDestroy$: ReplaySubject<void>;

  constructor(
    @Inject(NZ_MODAL_DATA) public modalData: { referralId: number; name: string },
    private _modal: NzModalRef,
    private _invitationService: InvitationsService,
    private _authService: AuthService,
    private _messageService: CustomMessageService,
    private _router: Router,
    private _translateService: TranslateService
  ) {
    this.isLoading = false;
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onCancelClick(): void {
    this._modal.destroy();
  }

  public onDeleteClick(): void {
    const { referralId, name } = this.modalData;
    this.isLoading = true;
    this._invitationService
      .deleteInvitation(referralId)
      .pipe(
        switchMap(() => this._authService.getMe(true).pipe(catchError(() => of(null)))),
        tap(() => {
          this._translateService.get('common.ITEM_REMOVED_SUCCESS', { name }).subscribe((localizedMessage: string) => {
            this._messageService.success(localizedMessage);
          });
        }),
        catchError(() => {
          this._translateService.get('common.ITEM_REMOVED_FAILURE').subscribe((localizedMessage: string) => {
            this._messageService.error(localizedMessage);
          });
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
          this._router.navigate(['/workspace/my-referrals']);
          this._modal.destroy();
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }
}
